import React, { Children } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Grid
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FormattedMessage } from "react-intl";
import { dateStyle, formatNumberShort } from "../../../components/formatter";
import Spinner from "../../../components/Spinner/Spinner";
import { useParcels } from "../../../services/clear-customs/overall";

export function ParcelsHandled() {
  const { parcelsHandled, isLoading } = useParcels();

  return (
    <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
      {isLoading &&
        <Grid container spacing={2}>
          {Children.toArray(
            [...Array(4).keys()].map((_, index) => {
              return (
                <Grid key={index} item xs={12} sm={6} md={3} lg={3}>
                  <Box sx={{ textAlign: "center" }}>
                    <Spinner size={"36px"} />
                  </Box>
                </Grid>
              );
            })
          )}
        </Grid>
      }

      {!isLoading &&
        <>
          <Box>
            <Grid container spacing={2}>
              {parcelsHandled?.map?.((tp, index) => (
                <>
                  <Grid key={index} item xs={12} sm={6} md={3} lg={3}>
                    <Card
                      sx={{
                        height: "105px",
                        alignSelf: "stretch",
                        flexGrow: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        padding: "16px 16px 10px",
                        borderRadius: "12px",
                        boxShadow: "0 1px 3px 0 var(--10), 0 1px 2px -1px var(--10)",
                        border: "solid 1px var(--new-border)",
                        backgroundColor: "var(--font-heavy-default)",
                      }}
                    >
                      <CardContent
                        sx={{
                          width: "100%",
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          alignItems: "flex-start",
                          gap: "8px",
                          padding: 0,
                          "&:last-child": { paddingBottom: 0 },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "20px",
                            fontWeight: 600,
                            color: "var(--app-body)",
                          }}
                          data-testid={`parcels_handled_box${index}_heading`}
                        >
                          <FormattedMessage id="parcelHandled" />
                          <Tooltip title="Total parcels processed in the selected period">
                            <IconButton
                              size="small"
                              sx={{ color: "var(--app-body)", ml: 0.5 }}
                              data-testid={`parcels_handled_box${index}_tooltip`}
                            >
                              <InfoOutlinedIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        {tp?.parcelCount &&
                          <Typography
                            sx={{
                              fontFamily: "Inter",
                              fontSize: "28px",
                              fontWeight: 600,
                              textAlign: "right",
                              color: "var(--app-body)",
                            }}
                            noWrap
                            data-testid={`parcels_handled_box${index}_data`}
                          >
                            <Tooltip
                              placement="right"
                              title={tp?.parcelCount?.toLocaleString?.("de-DE")}>
                              {formatNumberShort(tp?.parcelCount)} KG
                            </Tooltip>
                          </Typography>
                        }
                        {!tp?.parcelCount &&
                          <Typography
                            sx={{ mt: 1, color: "var(--app-body)" }}
                            noWrap
                            data-testid={`parcels_handled_box${index}_data`}
                          >
                            <FormattedMessage id="noData" />
                          </Typography>
                        }
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              ))}
            </Grid>
          </Box>
        </>
      }
    </Box>
  );
}

export function getMonthName(monthNumber) {
  return new Date(2000, monthNumber - 1, 1).toLocaleString("en-US", { month: "long" });
}

export const formatTitle = (tp) => {
  switch (tp?.type) {
    case "day":
      return tp?.date ? dateStyle(tp.date) : "";

    case "week":
      return tp?.start && tp?.end
        ? `${dateStyle(tp.start)} - ${dateStyle(tp.end)}`
        : "";

    case "month":
      return tp?.month && tp?.year
        ? `${getMonthName(tp.month)} ${tp.year}`
        : "";

    case "quarter":
      return tp?.quarter && tp?.year
        ? `${tp.quarter} ${tp.year}`
        : "";

    default:
      return "";
  }
};
